// components/UserFilter.tsx
import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  IconButton,
  Collapse,
  Chip,
  SelectChangeEvent,
  Button,
  alpha,
  useTheme,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import { UserType } from '../types/user';

interface UserFilterProps {
  onFilterChange: (filters: FilterState) => void;
  locations: Array<{ id: number; name: string }>;
}

export interface FilterState {
  search: string;
  location_id: 'all' | string;
  userType: UserType;
}

const UserFilter: React.FC<UserFilterProps> = ({ onFilterChange, locations }) => {
  const theme = useTheme();
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    location_id: 'all',
    userType: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFilters = { ...filters, search: event.target.value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleLocationChange = (event: SelectChangeEvent) => {
    const newFilters = { 
      ...filters, 
      location_id: event.target.value 
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleUserTypeChange = (event: SelectChangeEvent) => {
    const newFilters = { 
      ...filters, 
      userType: event.target.value as UserType
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters: FilterState = {
      search: '',
      location_id: 'all',
      userType: 'all'
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
    setShowFilters(false);
  };

  const clearSearch = () => {
    const newFilters = { ...filters, search: '' };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const activeFilterCount = Object.values(filters).filter(
    v => v !== 'all' && v !== ''
  ).length;

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 1.5, 
        mb: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          fullWidth
          placeholder="Search by name, email, or phone..."
          value={filters.search}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <IconButton 
                  size="small" 
                  onClick={clearSearch}
                  sx={{ 
                    p: 0.5,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.primary.main
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        
        <Badge
          color="primary"
          badgeContent={activeFilterCount}
          invisible={activeFilterCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 10,
              height: 18,
              minWidth: 18
            }
          }}
        >
          <IconButton 
            onClick={() => setShowFilters(!showFilters)}
            color={showFilters || activeFilterCount > 0 ? 'primary' : 'default'}
            sx={{ 
              bgcolor: showFilters ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
              borderRadius: 2,
              p: 1,
              '&:hover': {
                bgcolor: showFilters ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.action.hover, 0.1)
              }
            }}
          >
            <FilterIcon />
          </IconButton>
        </Badge>
      </Box>

      <Collapse in={showFilters}>
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          gap: 1.5, 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>User Type</InputLabel>
            <Select
              value={filters.userType}
              label="User Type"
              onChange={handleUserTypeChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon fontSize="small" />
                  All Users
                </Box>
              </MenuItem>
              <MenuItem value="customer">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  Customers Only
                </Box>
              </MenuItem>
              <MenuItem value="staff">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminIcon fontSize="small" />
                  Staff Only
                </Box>
              </MenuItem>
              <MenuItem value="wholesale">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorefrontIcon fontSize="small" />
                  Wholesale Customers
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={filters.location_id}
              label="Location"
              onChange={handleLocationChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StoreIcon fontSize="small" />
                  All Locations
                </Box>
              </MenuItem>
              {locations.map(location => (
                <MenuItem key={location.id} value={location.id.toString()}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {activeFilterCount > 0 && (
            <Button 
              size="small" 
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              variant="outlined"
              color="inherit"
              sx={{ 
                borderRadius: 2,
                ml: 'auto',
                borderColor: alpha(theme.palette.divider, 0.5),
                color: theme.palette.text.secondary,
                '&:hover': {
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.04)
                }
              }}
            >
              Clear All
            </Button>
          )}
        </Box>

        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {filters.userType !== 'all' && (
              <Chip
                size="small"
                label={`Type: ${filters.userType === 'customer' ? 'Customers' : 'Staff'}`}
                onDelete={() => {
                  const newFilters = { ...filters, userType: 'all' as UserType };
                  setFilters(newFilters);
                  onFilterChange(newFilters);
                }}
                sx={{ height: 24 }}
              />
            )}
            {filters.location_id !== 'all' && (
              <Chip
                size="small"
                label={`Location: ${locations.find(l => l.id.toString() === filters.location_id)?.name || 'Selected'}`}
                onDelete={() => {
                  const newFilters = { ...filters, location_id: 'all' };
                  setFilters(newFilters);
                  onFilterChange(newFilters);
                }}
                sx={{ height: 24 }}
              />
            )}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default UserFilter;